import { Component, computed, HostListener, inject, signal } from '@angular/core';
import { form, FormField, FormRoot, required, submit } from '@angular/forms/signals';
import { MatExpansionModule } from '@angular/material/expansion';
import { UserStatusComponent } from '@app/components/user/user-status/user-status.component';
import { DEFAULT_DATETIME_FORMAT } from '@app/shared/configs/date-format.config';
import { phoneNumber } from '@app/shared/forms/rules';
import { ProffeoDatePipe } from '@app/shared/pipes/proffeo-date.pipe';
import { AuthService } from '@app/shared/services/auth.service';
import { ToastService } from '@app/shared/services/shared/toast.service';
import { UpdateUserDetailsRequestBody } from '@app/shared/types/models/user/user-dto.model';
import { User } from '@app/shared/types/models/user/user.model';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';
import { UiInputComponent } from '@app/shared/ui/input/ui-input.component';
import { UiTextareaComponent } from '@app/shared/ui/textarea/ui-textarea.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

type BasicDataFormModel = Required<UpdateUserDetailsRequestBody>;

const toFormModel = (user: User | undefined): BasicDataFormModel => ({
  firstName: user?.firstName ?? '',
  lastName: user?.lastName ?? '',
  address: user?.address ?? '',
  phoneNumbers: user?.phoneNumbers ?? '',
  notes: user?.notes ?? ''
});

@Component({
  selector: 'proffeo-user-basic-data-view',
  templateUrl: './basic-data-view.component.html',
  styleUrl: './basic-data-view.component.css',
  imports: [
    FormField,
    FormRoot,
    MatExpansionModule,
    UiButtonComponent,
    TranslatePipe,
    UiInputComponent,
    UiTextareaComponent,
    UserStatusComponent,
    ProffeoDatePipe
  ]
})
export class BasicDataComponent {
  private readonly authService = inject(AuthService);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);

  protected readonly defaultDatetimeFormat = DEFAULT_DATETIME_FORMAT;
  protected readonly personalFields = computed(() => [
    { key: 'last-name-control', value: this.userData()?.lastName },
    { key: 'first-name-control', value: this.userData()?.firstName }
  ]);
  protected readonly contactFields = computed(() => [
    { key: 'email-control', value: this.userData()?.email },
    { key: 'phone-number-control', value: this.userData()?.phoneNumbers },
    { key: 'address-control', value: this.userData()?.address }
  ]);
  /** Draft of the editor; refilled from the current user each time editing starts (user refreshes never overwrite it). */
  protected readonly model = signal<BasicDataFormModel>(toFormModel(this.authService.currentUser()));
  // Rules carry no message: the view shows one shared alert per section instead of per-field messages.
  protected readonly form = form(
    this.model,
    path => {
      required(path.firstName);
      required(path.lastName);
      phoneNumber(path.phoneNumbers);
    },
    { submission: { action: () => this.save() } }
  );
  protected readonly showNameError = computed(() =>
    [this.form.firstName(), this.form.lastName()].some(field => field.touched() && field.invalid())
  );
  protected readonly showPhoneNumberError = computed(
    () => this.form.phoneNumbers().touched() && this.form.phoneNumbers().invalid()
  );

  public readonly editMode = signal(false);
  public readonly userData = this.authService.currentUser;
  public readonly saving = computed(() => this.form().submitting());
  public readonly hasUnsavedChanges = computed(() => this.editMode() && this.form().dirty());

  @HostListener('window:beforeunload', ['$event'])
  protected preventUnsavedClose(event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges() || this.saving()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  public discardChanges(): void {
    this.cancelEditMode();
  }

  protected toggleEditMode(): void {
    if (this.saving()) return;
    if (!this.editMode()) {
      this.model.set(toFormModel(this.userData()));
      this.form().reset();
    }
    this.editMode.update(editing => !editing);
  }

  protected cancelEditMode(): void {
    if (this.saving()) return;
    this.editMode.set(false);
  }

  protected updateUserBasicData(): void {
    void submit(this.form);
  }

  private async save(): Promise<void> {
    try {
      // A save superseded by a session change completes without a value - nothing to report then.
      const user = await firstValueFrom(this.authService.updateCurrentUser(this.model()), { defaultValue: null });
      if (!user) return;
      this.editMode.set(false);
      this.toastService.success(
        this.translate.instant('toasts.user-details-successfully-saved', { value: user.fullName })
      );
    } catch {
      this.toastService.error(this.translate.instant('toasts.user-details-edit-failed'));
    }
  }
}
